import { useEffect, useState } from "react";
import { firebaseAuth } from "../../../firebase";
import {
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  getAdditionalUserInfo,
  type AuthProvider,
} from "firebase/auth";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useFirebaseAuthentication from "../../hooks/useFirebaseAuthentication";
import SignUpForm from "./SignUpForm";
import { useMiscStore } from "../../stateDefs";

/** What a failed federated sign-in means, in the user's terms. */
const ssoMessage = (code: string): string => {
  switch (code) {
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "";
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in window. Allow pop-ups for this site and try again.";
    case "auth/unauthorized-domain":
      return "This site is not yet authorized for that sign-in method. Use email and password, or ask support@kono.ai.";
    case "auth/operation-not-allowed":
      return "That sign-in method is not switched on yet. Use email and password, or try Google.";
    case "auth/account-exists-with-different-credential":
      return "This address already has a Kono account. Sign in with the method you used originally (often email and password), then you can link others from your account.";
    default:
      return `Sign-in failed (${code}). Try another method or email and password.`;
  }
};

const GoogleMark = () => (
  <svg className="h-5 w-5 shrink-0" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 10-2 13.5-5.2l-6.2-5.2C29.3 35.3 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-4.1 5.6-7.3 6.6l6.2 5.2C36.5 41.3 44 36 44 24c0-1.3-.1-2.3-.4-3.5z"
    />
  </svg>
);

const MicrosoftMark = () => (
  <svg className="h-5 w-5 shrink-0" viewBox="0 0 23 23" aria-hidden="true">
    <path fill="#F25022" d="M1 1h10v10H1z" />
    <path fill="#7FBA00" d="M12 1h10v10H12z" />
    <path fill="#00A4EF" d="M1 12h10v10H1z" />
    <path fill="#FFB900" d="M12 12h10v10H12z" />
  </svg>
);

type SsoProvider = "google" | "microsoft";

const buildProvider = (which: SsoProvider): AuthProvider => {
  if (which === "google") {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    return provider;
  }
  const provider = new OAuthProvider("microsoft.com");
  provider.addScope("email");
  provider.addScope("openid");
  provider.addScope("profile");
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
};

export default function UserAuthPage({
  logoImg,
  requestAccessHref,
}: UserAuthPageProps) {
  const [ssoErrorCode, setSsoErrorCode] = useState("");
  const [ssoBusy, setSsoBusy] = useState<SsoProvider | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { authUser } = useFirebaseAuthentication();
  const emailPrefill = new URLSearchParams(location.search).get("email") || "";
  const namePrefill = new URLSearchParams(location.search).get("name") || "";
  const mode = location.pathname.includes("/signup")
    ? "signup"
    : location.pathname.includes("/forgot-password")
      ? "forgot-password"
      : "signin";
  const isForgotPassword = mode === "forgot-password";
  const title =
    mode === "signup"
      ? "Create your account"
      : mode === "forgot-password"
        ? "Reset your password"
        : "Sign in";
  const setCompanyDetailsRequired = useMiscStore(
    (state) => state.setCompanyDetailsRequired,
  );
  const showSso = process.env.REACT_APP_ENABLE_SSO === "true" && !isForgotPassword;

  const handleSso = (which: SsoProvider) => {
    if (ssoBusy) return;
    setSsoErrorCode("");
    setSsoBusy(which);
    firebaseAuth.useDeviceLanguage();
    signInWithPopup(firebaseAuth, buildProvider(which))
      .then((uc) => {
        const info = getAdditionalUserInfo(uc);
        if (info?.isNewUser) {
          setCompanyDetailsRequired(true);
        }
      })
      .catch((error) => {
        console.error(error);
        setSsoErrorCode(error.code || "auth/unknown");
      })
      .finally(() => setSsoBusy(null));
  };

  useEffect(() => {
    if (!!authUser) {
      const redirectPath = decodeURIComponent(
        new URLSearchParams(location.search).get("redirect") || "",
      );
      navigate(redirectPath || "/");
    }
  }, [authUser]);

  return (
    <div className="mx-auto md:w-[30em] px-4 py-10">
      <Link
        to="/"
        className="absolute top-4 right-4 lg:right-20 text-gray-500 hover:text-gray-700 text-2xl leading-none"
        title="Close"
      >
        ×
      </Link>
      <Link to="/">
        <img src={logoImg} className="mx-auto mb-10 w-auto h-[4em]" />
      </Link>

      <h1 className="text-xl font-semibold text-center mb-5">{title}</h1>

      {showSso && (
        <div className="mb-6 space-y-3">
          <button
            type="button"
            title="Sign in with Google"
            disabled={!!ssoBusy}
            onClick={() => handleSso("google")}
            className="flex w-full items-center justify-center gap-3 rounded-full border border-slate-800 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-60"
          >
            <GoogleMark />
            {ssoBusy === "google"
              ? "Opening Google…"
              : "Log in with Google"}
          </button>
          <button
            type="button"
            title="Sign in with Microsoft"
            disabled={!!ssoBusy}
            onClick={() => handleSso("microsoft")}
            className="flex w-full items-center justify-center gap-3 rounded-full border border-slate-800 bg-white px-4 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-60"
          >
            <MicrosoftMark />
            {ssoBusy === "microsoft"
              ? "Opening Microsoft…"
              : "Log in with Microsoft"}
          </button>
          {!!ssoErrorCode && (
            <div className="text-center text-sm text-red-600">
              {ssoMessage(ssoErrorCode)}
            </div>
          )}
          <div className="text-center text-sm text-slate-500 pt-1">
            or continue with email
          </div>
        </div>
      )}

      <SignUpForm
        mode={mode}
        emailPrefill={emailPrefill}
        namePrefill={namePrefill}
      />

      <div className="text-sm text-center mt-2 mb-6">
        {mode === "signin" && (
          <div className="space-y-1">
            {requestAccessHref ? (
              <div>
                Vendor without an account?{" "}
                <Link
                  to={requestAccessHref}
                  className="text-blue-700 hover:underline"
                >
                  Request access
                </Link>
              </div>
            ) : (
              <div>
                New to Kono?{" "}
                <Link to="/signup" className="text-blue-700 hover:underline">
                  Create account
                </Link>
              </div>
            )}
            <div>
              <Link
                to="/forgot-password"
                className="text-blue-700 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </div>
        )}
        {mode === "signup" && (
          <div>
            Already have an account?{" "}
            <Link to="/signin" className="text-blue-700 hover:underline">
              Sign in
            </Link>
          </div>
        )}
        {mode === "forgot-password" && (
          <div className="space-y-1">
            <div>
              Remembered your password?{" "}
              <Link to="/signin" className="text-blue-700 hover:underline">
                Sign in
              </Link>
            </div>
            <div>
              Need an account?{" "}
              <Link to="/signup" className="text-blue-700 hover:underline">
                Create account
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type UserAuthPageProps = {
  logoImg: string;
  /** Vendor site: where a company without an account asks for one. */
  requestAccessHref?: string;
};
